import { isRichTextValue } from "@/shared/utils/rich-text";
import type { RichText } from "@spz/shared-domain";
import { AttachmentPicker } from "@spz/ui/components/attachment-picker";
import { TextEditor } from "@spz/ui/components/text-editor";
import { useImperativeHandle } from "react";
import {
  type CommentFieldValue,
  useCommentFieldViewModel,
} from "./use-comment-field";

export type CommentFieldHandle = {
  reset: () => void;
};

export type CommentFieldProps = {
  companyId: string;
  initialValues?: Partial<CommentFieldValue>;
  allowedFileTypes?: string[];
  onChange: (values: {
    content: RichText;
    attachment: File | null;
  }) => void;
  onBlur?: () => void;
  submitBtn?: React.ReactNode;
  ref?: React.Ref<CommentFieldHandle>;
};

export const CommentField = ({
  companyId,
  initialValues,
  allowedFileTypes,
  submitBtn,
  ref,
  onChange,
  onBlur,
}: CommentFieldProps) => {
  const { t, editorRef, form, reset, mentions } = useCommentFieldViewModel({
    companyId,
    initialValues,
    allowedFileTypes,
    onChange,
    onBlur,
  });

  useImperativeHandle(ref, () => ({
    reset,
  }));

  return (
    <div className="w-full">
      <div className="relative w-full h-[200px] border border-gray-200 p-2 bg-white rounded-md hover:border-pink-200 focus-within:border-pink-300">
        {!mentions.loading && (
          <TextEditor
            ref={editorRef}
            placeholder={t("activity_comment_placeholder")}
            onContentChange={(jsonContent) => {
              if (!isRichTextValue(jsonContent)) {
                form.update({
                  content: undefined,
                });
                return;
              }

              form.update({
                content: jsonContent,
              });
            }}
            canMention={true}
            mentionTriggerChar="@"
            mentionableItems={mentions.items}
            noResultsMessage={t("no_user_found")}
            className="border-none h-[140px] overflow-y-auto"
          />
        )}

        <div className="!absolute bottom-1 left-2 right-2 flex items-center justify-between gap-2">
          <AttachmentPicker
            file={form.fields.attachment.value}
            onFileChange={(file) => {
              form.update({
                attachment: file,
              });
            }}
            allowedFileTypes={form.fields.attachment.allowedFileTypes}
            label={t("add_attachment")}
          />

          {submitBtn}
        </div>
      </div>
    </div>
  );
};
